import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { subscribeToBanquetOwnerAuth } from "../services/banquetOwnerAuthService.js";
import { isBanquetUserRole } from "../model/banquetOwnerModel.js";
import ChangePasswordScreen from "./ChangePasswordScreen.jsx";

function BanquetOwnerAccessDenied() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f7f4] px-4">
      <div className="max-w-md rounded-3xl border border-rose-200 bg-white px-8 py-10 text-center shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-rose-600">Access denied</p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">Banquet owner access required</h1>
        <p className="mt-2 text-sm text-slate-600">
          This dashboard is only available to active banquet owner, manager, or staff accounts.
        </p>
        <a
          href="/banquet-owner/login"
          className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-full bg-emerald-700 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
        >
          Go to banquet login
        </a>
      </div>
    </div>
  );
}

export default function BanquetOwnerProtectedRoute({ children }) {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isLoading: true,
    owner: null,
  });

  useEffect(() => {
    const unsubscribe = subscribeToBanquetOwnerAuth((owner) => {
      setAuthState({
        isLoading: false,
        owner,
      });
    });

    return unsubscribe;
  }, []);

  const noindexMeta = <meta name="robots" content="noindex, nofollow" />;

  if (authState.isLoading) {
    return (
      <>
        {noindexMeta}
        <div className="flex min-h-screen items-center justify-center bg-[#f5f7f4] px-4">
          <div className="rounded-3xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-600">Banquet Owner Panel</p>
            <h1 className="mt-3 text-2xl font-semibold text-slate-900">Checking access</h1>
            <p className="mt-2 text-sm text-slate-600">Auth state wiring is ready for Firebase Auth connection.</p>
          </div>
        </div>
      </>
    );
  }

  if (!authState.owner) {
    return <Navigate to="/banquet-owner/login" replace state={{ from: location }} />;
  }

  if (authState.owner.isActive === false || !isBanquetUserRole(authState.owner.role)) {
    return (
      <>
        {noindexMeta}
        <BanquetOwnerAccessDenied />
      </>
    );
  }

  if (authState.owner.mustChangePassword) {
    return (
      <>
        {noindexMeta}
        <ChangePasswordScreen
          onPasswordChanged={() =>
            setAuthState((current) => ({
              ...current,
              owner: { ...current.owner, mustChangePassword: false },
            }))
          }
        />
      </>
    );
  }

  return (
    <>
      {noindexMeta}
      {children}
    </>
  );
}
